import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  isSupabaseConfigured,
  supabase,
} from "../../lib/supabaseClient";

import {
  deleteRsvp,
  getRsvps,
  updateContacted,
} from "../../services/rsvpService";

import "./AdminPage.css";


const eventOptions = [
  {
    value: "Mehendi — Nov 19",
    label: "Mehendi · Nov 19",
  },
  {
    value: "Cocktail — Nov 20",
    label: "Cocktail · Nov 20",
  },
  {
    value: "Haldi — Nov 21",
    label: "Haldi · Nov 21",
  },
  {
    value: "Pellikuthuru — Nov 21",
    label: "Pellikuthuru · Nov 21",
  },
  {
    value: "Pellikoduku — Nov 21",
    label: "Pellikoduku · Nov 21",
  },
  {
    value: "Wedding — Nov 22",
    label: "Wedding · Nov 22",
  },
];


const csvColumns = [
  ["submitted_at", "Submitted"],
  ["guest_name", "Guest"],
  ["email", "Email"],
  ["phone", "Phone"],
  ["attending", "Attending"],
  ["adult_count", "Adults"],
  ["kids_count", "Kids"],
  [
    "events_attending",
    "Events Attending",
  ],
  [
    "vegetarian_count",
    "Vegetarian",
  ],
  [
    "non_vegetarian_count",
    "Non-Vegetarian",
  ],
  [
    "kids_meal_count",
    "Kids Meals",
  ],
  [
    "dietary_notes",
    "Dietary Notes",
  ],
  [
    "song_request",
    "Song Request",
  ],
  ["message", "Message"],
  ["contacted", "Contacted"],
];


const csvEscape = (value) => {
  const normalized =
    Array.isArray(value)
      ? value.join(" | ")
      : value ?? "";

  return `"${String(normalized).replaceAll(
    '"',
    '""'
  )}"`;
};


const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  try {
    return new Intl.DateTimeFormat(
      "en-US",
      {
        dateStyle: "medium",
        timeStyle: "short",
      }
    ).format(new Date(value));
  } catch {
    return value;
  }
};


/* =========================================================
   CLIPBOARD
   ========================================================= */

async function copyText(text) {
  if (
    navigator.clipboard &&
    window.isSecureContext
  ) {
    await navigator.clipboard.writeText(
      text
    );

    return;
  }

  const textArea =
    document.createElement("textarea");

  textArea.value = text;

  textArea.style.position = "fixed";
  textArea.style.opacity = "0";

  document.body.appendChild(
    textArea
  );

  textArea.focus();
  textArea.select();

  document.execCommand("copy");

  document.body.removeChild(
    textArea
  );
}


/* =========================================================
   REMINDER TEXT
   ========================================================= */

const thirtyDayReminder = `Subject: 30 days until Anjan & Sahana's wedding celebrations ♡

Hi everyone ♡

Only one month to go!

We are so excited to celebrate with you this November.

Here's a little reminder to keep our wedding celebrations on your calendar:

November 19 — Mehendi · Tales in Henna
November 20 — Cocktail · Dancing into Forever
November 21 — Haldi · Hues of Sunshine
November 21 — Pellikuthuru · Bride's Chapter
November 21 — Pellikoduku · Groom's Chapter
November 22 — The Wedding · Beginning of Forever

We cannot wait to celebrate, dance, laugh and make beautiful memories with you.

Love,
Anjan & Sahana ♡

#ANUkoJAaNmilgaya`;


const tenDayReminder = `Subject: Only 10 days until Anjan & Sahana's wedding celebrations ♡

Hi everyone ♡

Only 10 days to go!

The celebrations are almost here and we cannot wait to see you.

A quick reminder of our wedding week:

NOVEMBER 19
Mehendi · Tales in Henna
6:00 PM
2 Gatewood Dr, DeSoto, TX 75115

NOVEMBER 20
Cocktail · Dancing into Forever
6:00 PM
Prism

NOVEMBER 21
Haldi · Hues of Sunshine
9:00 AM
2 Gatewood Dr, DeSoto, TX 75115

NOVEMBER 21
Pellikuthuru · Bride's Chapter
7:00 PM
Location TBD

NOVEMBER 21
Pellikoduku · Groom's Chapter
7:00 PM
Location TBD

NOVEMBER 22
The Wedding · Beginning of Forever
Firefly Gardens
Time coming soon

We are counting down the days and cannot wait to celebrate with you!

Love,
Anjan & Sahana ♡

#ANUkoJAaNmilgaya`;


/* =========================================================
   LOGIN
   ========================================================= */

function AdminLogin() {
  const [email, setEmail] =
    useState("");

  const [password, setPassword] =
    useState("");

  const [status, setStatus] =
    useState("idle");

  const [message, setMessage] =
    useState("");


  const signIn = async (event) => {
    event.preventDefault();

    setStatus("loading");
    setMessage("");


    const { error } =
      await supabase.auth.signInWithPassword(
        {
          email: email
            .trim()
            .toLowerCase(),

          password,
        }
      );


    if (error) {
      console.error(
        "Admin login failed:",
        error
      );

      setStatus("error");

      setMessage(
        "The admin email or password is incorrect."
      );

      return;
    }


    setStatus("success");
  };


  return (
    <main className="adminLoginPage">
      <section className="adminLoginCard">

        <a
          className="adminBackLink"
          href="/"
        >
          ← Back to invitation
        </a>

        <p className="adminEyebrow">
          private space
        </p>

        <h1>RSVP Admin</h1>

        <p className="adminLoginIntro">
          Sign in using your private
          Supabase admin account.
        </p>


        <form onSubmit={signIn}>
          <label>
            Admin email

            <input
              type="email"
              value={email}
              onChange={(event) =>
                setEmail(
                  event.target.value
                )
              }
              autoComplete="email"
              required
            />
          </label>


          <label>
            Password

            <input
              type="password"
              value={password}
              onChange={(event) =>
                setPassword(
                  event.target.value
                )
              }
              autoComplete="current-password"
              required
            />
          </label>


          <button
            type="submit"
            disabled={
              status === "loading"
            }
          >
            {status === "loading"
              ? "Opening dashboard..."
              : "Open dashboard"}
          </button>


          {message && (
            <p className="adminError">
              {message}
            </p>
          )}
        </form>

      </section>
    </main>
  );
}


/* =========================================================
   SUMMARY
   ========================================================= */

function SummaryCard({
  label,
  value,
  note,
}) {
  return (
    <article className="adminSummaryCard">
      <span>{label}</span>

      <strong>{value}</strong>

      {note && (
        <small>{note}</small>
      )}
    </article>
  );
}


/* =========================================================
   DASHBOARD
   ========================================================= */

function AdminDashboard() {
  const [rsvps, setRsvps] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [
    attendanceFilter,
    setAttendanceFilter,
  ] = useState("All");

  const [
    eventFilter,
    setEventFilter,
  ] = useState("All");

  const [
    copyStatus,
    setCopyStatus,
  ] = useState("");


  /* =======================================================
     LOAD RSVP DATA
     ======================================================= */

  const loadData =
    useCallback(async () => {
      setLoading(true);

      setError("");

      try {
        const data =
          await getRsvps();

        setRsvps(data);
      } catch (loadError) {
        console.error(
          "Could not load RSVPs:",
          loadError
        );

        setError(
          "Could not load RSVPs. Make sure you are logged in using the approved Supabase admin account."
        );
      } finally {
        setLoading(false);
      }
    }, []);


  useEffect(() => {
    loadData();
  }, [loadData]);


  /* =======================================================
     LATEST RSVP PER EMAIL

     Important:
     If somebody RSVPs Yes and later changes to No,
     we use only their newest response.
     ======================================================= */

  const latestRsvps =
    useMemo(() => {
      const latestByEmail =
        new Map();

      /*
       * getRsvps() already returns newest first.
       */
      rsvps.forEach((rsvp) => {
        const email =
          String(
            rsvp.email || ""
          )
            .trim()
            .toLowerCase();

        if (!email) {
          return;
        }

        if (
          !latestByEmail.has(
            email
          )
        ) {
          latestByEmail.set(
            email,
            rsvp
          );
        }
      });

      return Array.from(
        latestByEmail.values()
      );
    }, [rsvps]);


  const attendingLatestRsvps =
    useMemo(
      () =>
        latestRsvps.filter(
          (rsvp) =>
            rsvp.attending === "Yes"
        ),
      [latestRsvps]
    );


  /* =======================================================
     SUMMARY
     ======================================================= */

  const summary =
    useMemo(() => {
      const attending =
        latestRsvps.filter(
          (rsvp) =>
            rsvp.attending === "Yes"
        );

      const declined =
        latestRsvps.filter(
          (rsvp) =>
            rsvp.attending === "No"
        );


      const sum = (field) =>
        attending.reduce(
          (total, rsvp) =>
            total +
            Number(
              rsvp[field] || 0
            ),
          0
        );


      const eventSelections =
        attending.reduce(
          (total, rsvp) =>
            total +
            (
              rsvp.events_attending
                ?.length || 0
            ),
          0
        );


      return {
        responses:
          latestRsvps.length,

        attendingParties:
          attending.length,

        declined:
          declined.length,

        adults:
          sum("adult_count"),

        kids:
          sum("kids_count"),

        vegetarian:
          sum(
            "vegetarian_count"
          ),

        nonVegetarian:
          sum(
            "non_vegetarian_count"
          ),

        kidsMeals:
          sum(
            "kids_meal_count"
          ),

        eventSelections,
      };
    }, [latestRsvps]);


  /* =======================================================
     EVENT COUNTS
     ======================================================= */

  const eventCounts =
    useMemo(() => {
      return eventOptions.map(
        (eventOption) => {
          const responses =
            attendingLatestRsvps.filter(
              (rsvp) =>
                rsvp.events_attending?.includes(
                  eventOption.value
                )
            );


          const people =
            responses.reduce(
              (total, rsvp) =>
                total +
                Number(
                  rsvp.adult_count || 0
                ) +
                Number(
                  rsvp.kids_count || 0
                ),
              0
            );


          return {
            ...eventOption,

            parties:
              responses.length,

            people,
          };
        }
      );
    }, [
      attendingLatestRsvps,
    ]);


  /* =======================================================
     FILTER
     ======================================================= */

  const filteredRsvps =
    useMemo(() => {
      const term =
        search
          .trim()
          .toLowerCase();


      return latestRsvps.filter(
        (rsvp) => {
          const searchable = [
            rsvp.guest_name,
            rsvp.email,
            rsvp.phone,
            rsvp.message,
            rsvp.song_request,

            ...(
              rsvp.events_attending ||
              []
            ),
          ];


          const matchesSearch =
            !term ||
            searchable
              .filter(Boolean)
              .some((value) =>
                String(value)
                  .toLowerCase()
                  .includes(term)
              );


          const matchesAttendance =
            attendanceFilter ===
              "All" ||
            rsvp.attending ===
              attendanceFilter;


          const matchesEvent =
            eventFilter ===
              "All" ||
            rsvp.events_attending?.includes(
              eventFilter
            );


          return (
            matchesSearch &&
            matchesAttendance &&
            matchesEvent
          );
        }
      );
    }, [
      latestRsvps,
      search,
      attendanceFilter,
      eventFilter,
    ]);


  /* =======================================================
     REMINDER BUTTONS
     ======================================================= */

  const copyAttendingEmails =
    async () => {
      const emails =
        attendingLatestRsvps
          .map((rsvp) =>
            String(
              rsvp.email || ""
            ).trim()
          )
          .filter(Boolean);


      if (!emails.length) {
        setCopyStatus(
          "No attending guest emails found."
        );

        return;
      }


      /*
       * Comma separated works nicely
       * when pasted into Gmail BCC.
       */
      await copyText(
        emails.join(", ")
      );


      setCopyStatus(
        `${emails.length} attending guest email${
          emails.length === 1
            ? ""
            : "s"
        } copied. Paste them into BCC in your wedding Gmail ♡`
      );
    };


  const copyThirtyDayReminder =
    async () => {
      await copyText(
        thirtyDayReminder
      );

      setCopyStatus(
        "30-day reminder copied ♡ Paste it into your wedding Gmail."
      );
    };


  const copyTenDayReminder =
    async () => {
      await copyText(
        tenDayReminder
      );

      setCopyStatus(
        "10-day reminder copied ♡ Paste it into your wedding Gmail."
      );
    };


  /* =======================================================
     CONTACTED
     ======================================================= */

  const toggleContacted =
    async (rsvp) => {
      setError("");

      try {
        const nextValue =
          !rsvp.contacted;

        await updateContacted(
          rsvp.id,
          nextValue
        );


        setRsvps((current) =>
          current.map((item) =>
            item.id === rsvp.id
              ? {
                  ...item,

                  contacted:
                    nextValue,
                }
              : item
          )
        );
      } catch (updateError) {
        console.error(
          "Contacted update failed:",
          updateError
        );

        setError(
          "Could not update the contacted status."
        );
      }
    };


  /* =======================================================
     DELETE
     ======================================================= */

  const removeRsvp =
    async (rsvp) => {
      const confirmed =
        window.confirm(
          `Delete the RSVP from ${rsvp.guest_name}? This cannot be undone.`
        );


      if (!confirmed) {
        return;
      }


      setError("");


      try {
        await deleteRsvp(
          rsvp.id
        );


        setRsvps((current) =>
          current.filter(
            (item) =>
              item.id !== rsvp.id
          )
        );
      } catch (deleteError) {
        console.error(
          "RSVP delete failed:",
          deleteError
        );

        setError(
          "Could not delete that RSVP."
        );
      }
    };


  /* =======================================================
     CSV
     ======================================================= */

  const exportCsv = () => {
    const header =
      csvColumns
        .map(([, label]) =>
          csvEscape(label)
        )
        .join(",");


    const rows =
      filteredRsvps.map(
        (rsvp) =>
          csvColumns
            .map(([key]) =>
              csvEscape(
                rsvp[key]
              )
            )
            .join(",")
      );


    const csv =
      [
        header,
        ...rows,
      ].join("\n");


    const blob =
      new Blob(
        [csv],
        {
          type:
            "text/csv;charset=utf-8",
        }
      );


    const url =
      URL.createObjectURL(
        blob
      );


    const link =
      document.createElement(
        "a"
      );


    link.href = url;

    link.download =
      `anjan-sahana-rsvps-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;


    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    URL.revokeObjectURL(url);
  };


  const signOut =
    async () => {
      await supabase.auth
        .signOut();
    };


  return (
    <main className="adminDashboard">

      {/* HEADER */}

      <header className="adminHeader">
        <div>
          <p className="adminEyebrow">
            Anjan & Sahana
          </p>

          <h1>
            RSVP Dashboard
          </h1>

          <p>
            Guest counts,
            celebrations, meals,
            songs and messages.
          </p>
        </div>


        <div className="adminHeaderActions">
          <a href="/">
            View invitation
          </a>

          <button
            type="button"
            onClick={signOut}
          >
            Sign out
          </button>
        </div>
      </header>


      {/* SUMMARY */}

      <section className="adminSummaryGrid">

        <SummaryCard
          label="Responses"
          value={
            summary.responses
          }
        />


        <SummaryCard
          label="Attending parties"
          value={
            summary.attendingParties
          }
          note={`${summary.declined} declined`}
        />


        <SummaryCard
          label="Guests"
          value={
            summary.adults +
            summary.kids
          }
          note={`${summary.adults} adults · ${summary.kids} kids`}
        />


        <SummaryCard
          label="Meals"
          value={
            summary.vegetarian +
            summary.nonVegetarian +
            summary.kidsMeals
          }
          note={`${summary.vegetarian} veg · ${summary.nonVegetarian} non-veg · ${summary.kidsMeals} kids`}
        />


        <SummaryCard
          label="Event RSVPs"
          value={
            summary.eventSelections
          }
          note="across all six celebrations"
        />

      </section>


      {/* ===============================================
          SIMPLE REMINDERS
          =============================================== */}

      <section className="adminReminderPanel">

        <div className="adminReminderHeader">
          <p className="adminEyebrow">
            wedding reminders
          </p>

          <h2>
            Reminder Helper ♡
          </h2>

          <p>
            Nothing is sent automatically.
            These buttons only copy what
            you need. Open your separate
            wedding Gmail and send using
            BCC.
          </p>
        </div>


        <div className="adminReminderButtons">

          <button
            type="button"
            onClick={
              copyAttendingEmails
            }
          >
            <strong>
              Copy Attending Emails
            </strong>

            <span>
              {
                attendingLatestRsvps.length
              }{" "}
              email
              {attendingLatestRsvps.length ===
              1
                ? ""
                : "s"}
            </span>
          </button>


          <button
            type="button"
            onClick={
              copyThirtyDayReminder
            }
          >
            <strong>
              Copy 30-Day Reminder
            </strong>

            <span>
              October 23, 2026
            </span>
          </button>


          <button
            type="button"
            onClick={
              copyTenDayReminder
            }
          >
            <strong>
              Copy 10-Day Reminder
            </strong>

            <span>
              November 12, 2026
            </span>
          </button>

        </div>


        {copyStatus && (
          <p className="adminCopyStatus">
            ✓ {copyStatus}
          </p>
        )}


        <div className="adminReminderInstructions">
          <strong>
            How to send:
          </strong>

          <span>
            1. Copy Attending Emails
          </span>

          <span>
            2. Open your wedding Gmail
          </span>

          <span>
            3. Paste emails into BCC
          </span>

          <span>
            4. Copy the reminder
          </span>

          <span>
            5. Paste, review and Send ♡
          </span>
        </div>

      </section>


      {/* EVENT COUNTS */}

      <section className="adminFunGrid">

        <article>
          <h2>
            Celebration attendance ♡
          </h2>

          <ol>
            {eventCounts.map(
              (event) => (
                <li
                  key={
                    event.value
                  }
                >
                  <strong>
                    {event.label}
                  </strong>

                  <span>
                    {" "}
                    —{" "}
                    {event.parties}{" "}
                    parties ·{" "}
                    {event.people}{" "}
                    guests
                  </span>
                </li>
              )
            )}
          </ol>
        </article>


        <article>
          <h2>
            Quick overview
          </h2>

          <p>
            Use the event filter below
            to see exactly who is
            attending each celebration.
          </p>

          <p>
            Currently showing{" "}
            <strong>
              {
                filteredRsvps.length
              }
            </strong>{" "}
            response
            {filteredRsvps.length ===
            1
              ? ""
              : "s"}.
          </p>
        </article>

      </section>


      {/* FILTERS */}

      <section className="adminToolbar">

        <input
          type="search"
          placeholder="Search guest, email, phone, event, song..."
          value={search}
          onChange={(event) =>
            setSearch(
              event.target.value
            )
          }
        />


        <select
          value={
            attendanceFilter
          }
          onChange={(event) =>
            setAttendanceFilter(
              event.target.value
            )
          }
        >
          <option value="All">
            All responses
          </option>

          <option value="Yes">
            Attending
          </option>

          <option value="No">
            Not attending
          </option>
        </select>


        <select
          value={eventFilter}
          onChange={(event) =>
            setEventFilter(
              event.target.value
            )
          }
        >
          <option value="All">
            All events
          </option>

          {eventOptions.map(
            (eventOption) => (
              <option
                key={
                  eventOption.value
                }
                value={
                  eventOption.value
                }
              >
                {
                  eventOption.label
                }
              </option>
            )
          )}
        </select>


        <button
          type="button"
          onClick={loadData}
        >
          Refresh
        </button>


        <button
          type="button"
          onClick={exportCsv}
          disabled={
            !filteredRsvps.length
          }
        >
          Export CSV
        </button>

      </section>


      {error && (
        <p className="adminBannerError">
          {error}
        </p>
      )}


      {/* TABLE */}

      <section className="adminTableCard">

        {loading ? (
          <p className="adminEmpty">
            Loading RSVPs...
          </p>
        ) : filteredRsvps.length ===
          0 ? (
          <p className="adminEmpty">
            No RSVPs match these
            filters.
          </p>
        ) : (
          <div className="adminTableScroll">

            <table>

              <thead>
                <tr>
                  <th>Guest</th>
                  <th>Attendance</th>
                  <th>Party</th>
                  <th>Events</th>
                  <th>Meals</th>
                  <th>
                    Song & Message
                  </th>
                  <th>Follow-up</th>
                </tr>
              </thead>


              <tbody>

                {filteredRsvps.map(
                  (rsvp) => (
                    <tr
                      key={
                        rsvp.id
                      }
                    >

                      <td>
                        <strong>
                          {
                            rsvp.guest_name
                          }
                        </strong>

                        <span>
                          {
                            rsvp.email
                          }
                        </span>

                        <span>
                          {rsvp.phone ||
                            "No phone"}
                        </span>

                        <small>
                          {formatDate(
                            rsvp.submitted_at
                          )}
                        </small>
                      </td>


                      <td>
                        <span
                          className={`statusPill ${
                            rsvp.attending ===
                            "Yes"
                              ? "yes"
                              : "no"
                          }`}
                        >
                          {rsvp.attending ===
                          "Yes"
                            ? "Attending"
                            : "Declined"}
                        </span>
                      </td>


                      <td>
                        {rsvp.attending ===
                        "Yes" ? (
                          <>
                            <strong>
                              {Number(
                                rsvp.adult_count ||
                                  0
                              )}{" "}
                              adults
                            </strong>

                            <span>
                              {Number(
                                rsvp.kids_count ||
                                  0
                              )}{" "}
                              kids
                            </span>
                          </>
                        ) : (
                          <span>—</span>
                        )}
                      </td>


                      <td className="wideCell">
                        {rsvp
                          .events_attending
                          ?.length ? (
                          rsvp.events_attending.map(
                            (
                              eventName
                            ) => (
                              <span
                                key={
                                  eventName
                                }
                              >
                                ✓{" "}
                                {
                                  eventName
                                }
                              </span>
                            )
                          )
                        ) : (
                          <span>—</span>
                        )}
                      </td>


                      <td>
                        {rsvp.attending ===
                        "Yes" ? (
                          <>
                            <strong>
                              {Number(
                                rsvp.vegetarian_count ||
                                  0
                              )}{" "}
                              veg
                            </strong>

                            <span>
                              {Number(
                                rsvp.non_vegetarian_count ||
                                  0
                              )}{" "}
                              non-veg
                            </span>

                            <span>
                              {Number(
                                rsvp.kids_meal_count ||
                                  0
                              )}{" "}
                              kids meals
                            </span>

                            {rsvp.dietary_notes && (
                              <em>
                                {
                                  rsvp.dietary_notes
                                }
                              </em>
                            )}
                          </>
                        ) : (
                          <span>—</span>
                        )}
                      </td>


                      <td className="wideCell">

                        {rsvp.song_request && (
                          <span>
                            ♫ Song:{" "}
                            {
                              rsvp.song_request
                            }
                          </span>
                        )}

                        {rsvp.message && (
                          <em>
                            “
                            {rsvp.message}
                            ”
                          </em>
                        )}

                        {!rsvp.song_request &&
                          !rsvp.message && (
                            <span>
                              —
                            </span>
                          )}
                      </td>


                      <td>

                        <label className="contactedToggle">
                          <input
                            type="checkbox"
                            checked={
                              Boolean(
                                rsvp.contacted
                              )
                            }
                            onChange={() =>
                              toggleContacted(
                                rsvp
                              )
                            }
                          />

                          Contacted
                        </label>


                        <button
                          type="button"
                          className="deleteRsvpButton"
                          onClick={() =>
                            removeRsvp(
                              rsvp
                            )
                          }
                        >
                          Delete
                        </button>

                      </td>

                    </tr>
                  )
                )}

              </tbody>

            </table>
          </div>
        )}

      </section>


      {/* MESSAGES + SONGS */}

      <section className="adminFunGrid">

        <article>
          <h2>
            Guest messages ♡
          </h2>

          {latestRsvps.filter(
            (rsvp) =>
              rsvp.message
          ).length ? (
            latestRsvps
              .filter(
                (rsvp) =>
                  rsvp.message
              )
              .slice(0, 15)
              .map(
                (rsvp) => (
                  <blockquote
                    key={
                      rsvp.id
                    }
                  >
                    “
                    {
                      rsvp.message
                    }
                    ”

                    <cite>
                      —{" "}
                      {
                        rsvp.guest_name
                      }
                    </cite>
                  </blockquote>
                )
              )
          ) : (
            <p>
              No messages yet.
            </p>
          )}
        </article>


        <article>
          <h2>
            Dance-floor requests ♪
          </h2>

          {latestRsvps.filter(
            (rsvp) =>
              rsvp.song_request
          ).length ? (
            <ol>
              {latestRsvps
                .filter(
                  (rsvp) =>
                    rsvp.song_request
                )
                .map(
                  (rsvp) => (
                    <li
                      key={
                        rsvp.id
                      }
                    >
                      {
                        rsvp.song_request
                      }

                      <span>
                        {" "}
                        —{" "}
                        {
                          rsvp.guest_name
                        }
                      </span>
                    </li>
                  )
                )}
            </ol>
          ) : (
            <p>
              No song requests yet.
            </p>
          )}
        </article>

      </section>

    </main>
  );
}


/* =========================================================
   AUTH
   ========================================================= */

export default function AdminPage() {
  const [session, setSession] =
    useState(null);

  const [loading, setLoading] =
    useState(true);


  useEffect(() => {
    if (!supabase) {
      setLoading(false);

      return undefined;
    }


    supabase.auth
      .getSession()
      .then(({ data }) => {
        setSession(
          data.session
        );

        setLoading(false);
      });


    const {
      data: {
        subscription,
      },
    } =
      supabase.auth.onAuthStateChange(
        (
          _event,
          nextSession
        ) => {
          setSession(
            nextSession
          );

          setLoading(false);
        }
      );


    return () => {
      subscription.unsubscribe();
    };
  }, []);


  if (
    !isSupabaseConfigured
  ) {
    return (
      <main className="adminLoginPage">

        <section className="adminLoginCard">

          <a
            className="adminBackLink"
            href="/"
          >
            ← Back to invitation
          </a>

          <p className="adminEyebrow">
            setup required
          </p>

          <h1>
            Connect Supabase
          </h1>

          <p className="adminLoginIntro">
            Add
            REACT_APP_SUPABASE_URL
            and
            REACT_APP_SUPABASE_ANON_KEY
            to your .env file, then
            restart the React app.
          </p>

        </section>

      </main>
    );
  }


  if (loading) {
    return (
      <main className="adminLoading">
        Opening RSVP dashboard...
      </main>
    );
  }


  return session
    ? <AdminDashboard />
    : <AdminLogin />;
}